import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";
import {
  parsePassKeySessionResponse,
  parseSecureCardDetailsResponse,
} from "./orendaApplicationHelpers";
import { promptPasskeyAssertion, describePasskeyError } from "./orendaWebAuthn";
import { decryptSecureCardDetails } from "./orendaSecureDetailsCrypto";
import OrendaPasskeyEnroll from "./OrendaPasskeyEnroll";

// Modal that reveals the applicant's PAN / CVC / expiry via Orenda's
// secure-details flow:
//
//   1. Fetch passkey session (backend: getpasskeysession)
//      — may return either {cipher, secretKey} (step 1 only) or
//        {cipher, passkeySession, fido2options} (steps 1+2 chained)
//   2. If fido2options came back, prompt the user's device passkey
//      (Face ID / Touch ID / Windows Hello) and collect an assertion
//   3. Fetch secure card details (backend: getcardsecuredetails)
//      — send cipher, passkeySession, assertion
//   4. Show the decrypted details with a countdown that auto-hides
//      them after AUTO_HIDE_SECONDS.
//
// If any step fails, we surface a human message and let the user retry.

const AUTO_HIDE_SECONDS = 600; // 10 minutes

// Backend expiry can arrive as "MM/YY", "MM/YYYY", "M/D/YYYY" (the current
// backend formatter produces "10/1/2029"), "MMYY", etc. Normalise to MM/YY
// so the card face always reads the same way regardless of source.
const normaliseExpiry = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Already MM/YY — pass through.
  if (/^\d{2}\/\d{2}$/.test(s)) return s;
  // MM/YYYY — trim century.
  let m = s.match(/^(\d{1,2})\/(\d{2})(\d{2})$/);
  if (m) return `${m[1].padStart(2, "0")}/${m[3]}`;
  // M/D/YYYY — ignore the day, keep MM/YY. Backend sends this today.
  m = s.match(/^(\d{1,2})\/\d{1,2}\/(\d{2})(\d{2})$/);
  if (m) return `${m[1].padStart(2, "0")}/${m[3]}`;
  // MMYY — insert slash.
  if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}/${s.slice(2)}`;
  // MMYYYY.
  if (/^\d{6}$/.test(s)) return `${s.slice(0, 2)}/${s.slice(4)}`;
  // ISO YYYY-MM — pull out the parts.
  m = s.match(/^(\d{2})(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}`;
  // Unknown shape — surface verbatim rather than losing it.
  return s;
};

// Normalise whatever backend-cached shape we get into the same {pan, cvc2,
// expiry, printedName, cardId} the full reveal flow uses, so the renderer
// doesn't care how we got the values.
const normaliseCachedDetails = (raw) => {
  if (!raw) return null;
  const pan = raw.pan ?? raw.cardNumber ?? raw.CardNumber ?? null;
  const cvc2 = raw.cvc2 ?? raw.cvv ?? raw.Cvv ?? null;
  const expiry = normaliseExpiry(
    raw.expiry ?? raw.expiry_date ?? raw.expirtDate ?? raw.ExpirtDate ?? null
  );
  if (!pan || !cvc2) return null; // only use the cache when BOTH are populated
  return {
    pan,
    cvc2,
    expiry,
    printedName: raw.printedName ?? raw.name ?? null,
    cardId: raw.cardId ?? null,
  };
};

const OrendaCardReveal = ({ open, onClose, cachedCardDetails }) => {
  const [phase, setPhase] = useState("idle");
  // idle | fetching-session | waiting-passkey | fetching-details |
  // ready | error | expired | needs-enroll
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_HIDE_SECONDS);
  const [cvcHidden, setCvcHidden] = useState(true);
  const [panHidden, setPanHidden] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const timerRef = useRef(null);
  const dialogRef = useRef(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setError(null);
    setDetails(null);
    setSecondsLeft(AUTO_HIDE_SECONDS);
    setCvcHidden(true);
    setPanHidden(false);
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Auto-start the flow when the modal opens.
  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    runFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cachedCardDetails]);

  // Countdown when details are visible → auto-hide + clear.
  useEffect(() => {
    if (phase !== "ready") return;
    setSecondsLeft(AUTO_HIDE_SECONDS);
    timerRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
          setDetails(null);
          setPhase("expired");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ───── Flow ──────────────────────────────────────────────
  const runFlow = useCallback(async () => {
    setError(null);
    setDetails(null);

    // Fast path: the viewApplication response may already carry the
    // decrypted card details (backend decrypts + stashes them on first
    // successful reveal). When present, skip the whole passkey + /secure
    // round-trip and render them directly.
    const cached = normaliseCachedDetails(cachedCardDetails);
    if (cached) {
      setDetails(cached);
      setPhase("ready");
      return;
    }

    setPhase("fetching-session");

    try {
      // Step 1+2 — passkey session (backend chains /secure-key with
      // /passkey-challenge and returns the fido2options we need).
      const sessionResp = await orendaService.getPassKeySessionOrenda();
      const session = parsePassKeySessionResponse(sessionResp);

      // The session is valid if we have either:
      //   • a cipher (traditional step-1-only response), OR
      //   • a passkeySession + fido2options (chained response — backend
      //     manages the cipher server-side).
      const hasChainedPasskey = session.passkeySession && session.fido2options;
      if (!session.cipher && !hasChainedPasskey) {
        throw new Error(
          "The server didn't return a usable secure-details session. Try again."
        );
      }

      // v2 step-up challenge does NOT echo allowCredentials — Orenda relies
      // on discoverable (resident) credentials registered under the rpId, so
      // omitting allowCredentials is correct behaviour and means "any
      // matching credential". We no longer try to pre-detect "no passkey"
      // from allowCredentials: if the user has none, the browser's
      // credentials.get() will NotAllowedError and we route to needs-enroll
      // from the catch branch below.
      let assertion = null;
      let passkeySession = session.passkeySession ?? null;

      if (session.fido2options) {
        setPhase("waiting-passkey");
        try {
          assertion = await promptPasskeyAssertion(session.fido2options);
        } catch (err) {
          // NotAllowedError with no credential on file → user genuinely has
          // no passkey for this rpId → surface the enroll CTA, not an error.
          if (err?.name === "NotAllowedError") {
            setPhase("needs-enroll");
            return;
          }
          throw new Error(describePasskeyError(err));
        }
      }

      // Step 3 — secure card details. Orenda rejects with
      // "VIEW_SECURE_CARD_DATA_FAILED: Cipher is required" if cipher is
      // missing or empty, so refuse to send the call rather than burn
      // the single-use passkeySession on a known-bad request.
      if (!session.cipher) {
        throw new Error(
          "The server didn't return a cipher for this session. Please try again."
        );
      }
      if (!session.secretKey) {
        throw new Error(
          "The server didn't return a decrypt key for this session. Please try again."
        );
      }
      setPhase("fetching-details");
      const detailsResp = await orendaService.getCardSecureDetailsOrenda({
        cipher: session.cipher,
        assertion,
        passkeySession,
        secretKey: session.secretKey,
      });
      const encrypted = parseSecureCardDetailsResponse(detailsResp);
      if (!encrypted?.pan) {
        throw new Error("Card details were not returned by the server.");
      }

      // Step 4 — decrypt pan / cvc / expiry client-side. Orenda uses
      // AES-CFB with `secretKey` as the AES key (confirmed by Christian
      // 2026-10-20). See orendaSecureDetailsCrypto.js for layout handling.
      let decrypted;
      try {
        decrypted = await decryptSecureCardDetails(
          encrypted,
          session.secretKey
        );
      } catch (err) {
        throw new Error(
          "Couldn't decrypt your card details. Please try again. " +
            `(${err?.message || err})`
        );
      }
      setDetails({
        pan: decrypted.pan,
        cvc2: decrypted.cvc2,
        expiry: decrypted.expiry,
        printedName: decrypted.printedName,
        cardId: decrypted.cardId,
      });
      setPhase("ready");
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setError("Your session expired. Please sign in again.");
      } else {
        setError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
      }
      setPhase("error");
    }
    // runFlow re-captures cachedCardDetails whenever the application
    // response is refetched, so the fast path kicks in as soon as the
    // backend starts returning cached values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedCardDetails]);

  if (!open) return null;

  const copyPan = () => {
    if (!details?.pan) return;
    try {
      navigator.clipboard.writeText(details.pan.replace(/\s+/g, ""));
    } catch { /* ignore */ }
  };

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-reveal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="orenda-reveal" ref={dialogRef}>
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">Secure card details</div>
            <h2 id="orenda-reveal-title" className="orenda-reveal-title">
              Your card
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="orenda-reveal-body">
          {phase === "fetching-session" && (
            <Spinner label="Preparing secure session…" />
          )}
          {phase === "waiting-passkey" && (
            <Spinner label="Confirm with your passkey to continue…" />
          )}
          {phase === "fetching-details" && (
            <Spinner label="Decrypting your card details…" />
          )}

          {phase === "ready" && details && (
            <>
              <FieldRow
                label="Card number"
                value={details.pan}
                mono
                monospaceGroups
                hidden={panHidden}
                onToggle={() => setPanHidden((h) => !h)}
                onCopy={copyPan}
              />
              <div className="orenda-reveal-grid">
                <FieldRow
                  label="Expiry"
                  value={details.expiry}
                  mono
                  smallGap
                />
                <FieldRow
                  label="CVC"
                  value={details.cvc2}
                  mono
                  hidden={cvcHidden}
                  onToggle={() => setCvcHidden((h) => !h)}
                  smallGap
                />
              </div>
              {/* Only render the cardholder row when Orenda returned a
                  printedName. Some card variants don't carry an embossed
                  name on the plastic — rendering an empty "—" cell there
                  just adds noise. */}
              {details.printedName && (
                <FieldRow
                  label="Cardholder"
                  value={details.printedName}
                />
              )}
              <div className="orenda-reveal-countdown">
                These details will clear in{" "}
                <strong>{formatCountdown(secondsLeft)}</strong> for your security.
              </div>
            </>
          )}

          {phase === "expired" && (
            <div className="orenda-reveal-expired">
              <h3 className="orenda-reveal-expired-title">
                Details hidden for your safety
              </h3>
              <p className="orenda-reveal-expired-sub">
                Click below to re-verify and view them again.
              </p>
              <button
                type="button"
                className="orenda-btn-primary"
                onClick={runFlow}
              >
                Show again
              </button>
            </div>
          )}

          {phase === "needs-enroll" && (
            <div className="orenda-reveal-error">
              <h3 className="orenda-reveal-expired-title">
                Set up a passkey first
              </h3>
              <p className="orenda-reveal-expired-sub">
                To see your full card number, we need a one-time passkey
                setup on this device. It's quick and keeps your card safe.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="orenda-btn-primary"
                  onClick={() => setEnrollOpen(true)}
                >
                  Set up passkey
                </button>
                <button
                  type="button"
                  className="orenda-btn-ghost"
                  onClick={onClose}
                >
                  Not now
                </button>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="orenda-reveal-error">
              <h3 className="orenda-reveal-expired-title">
                Couldn't show card details
              </h3>
              <p className="orenda-reveal-expired-sub">{error}</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="orenda-btn-primary"
                  onClick={runFlow}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="orenda-btn-ghost"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <OrendaPasskeyEnroll
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onEnrolled={() => {
          setEnrollOpen(false);
          // Passkey is now registered — re-run the reveal flow and it
          // should hit the normal passkey-challenge → details path.
          runFlow();
        }}
      />
    </div>
  );
};

// Format PAN in 4-4-4-4 groups when visible, mask when hidden.
const formatPan = (pan, hidden) => {
  if (!pan) return "";
  if (hidden) return "•••• •••• •••• " + pan.replace(/\s+/g, "").slice(-4);
  return pan.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
};

const FieldRow = ({ label, value, mono, monospaceGroups, hidden, onToggle, onCopy, smallGap }) => {
  const display =
    monospaceGroups && typeof value === "string"
      ? formatPan(value, hidden)
      : hidden && typeof value === "string"
      ? value.replace(/./g, "•")
      : value;
  return (
    <div className={`orenda-reveal-field${smallGap ? " orenda-reveal-field--sm" : ""}`}>
      <div className="orenda-reveal-field-label">{label}</div>
      <div className="orenda-reveal-field-row">
        <div
          className={`orenda-reveal-field-value${mono ? " orenda-reveal-field-value--mono" : ""}`}
        >
          {display || "—"}
        </div>
        <div className="orenda-reveal-field-actions">
          {onToggle && (
            <button
              type="button"
              className="orenda-reveal-icon-btn"
              onClick={onToggle}
              aria-label={hidden ? "Show" : "Hide"}
              title={hidden ? "Show" : "Hide"}
            >
              {hidden ? "Show" : "Hide"}
            </button>
          )}
          {onCopy && (
            <button
              type="button"
              className="orenda-reveal-icon-btn"
              onClick={onCopy}
              title="Copy to clipboard"
            >
              Copy
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Format seconds as MM:SS once we're over a minute; "30s" style otherwise.
const formatCountdown = (totalSeconds) => {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const Spinner = ({ label }) => (
  <div className="orenda-reveal-spinner">
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-label">{label}</div>
  </div>
);

export default OrendaCardReveal;
